import { saveAs } from 'file-saver';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { BulkImportStatusType } from '~/enums/BulkImportStatusType';
import { BulkImportUserResultType } from '~/enums/BulkImportUserResultType';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { Uuid } from '~/types/Uuid';

import { ImportStatus } from './ImportStatus';

interface Props {
  /** should be provided if used by super admin */
  clientId?: PilotId;
  /** active import id to get status */
  activeImportId: Nullable<Uuid>;
}

export const ImportUserStatus = ({
  clientId: clientIdParam,
  activeImportId,
}: Props) => {
  const currentUser = useCurrentUser();
  const formatDate = useFormatDate();
  const [hasActiveImportFinished, setHasActiveImportFinished] = useState(false);
  const clientId = clientIdParam ?? currentUser.client;
  const shouldDoPolling = activeImportId && !hasActiveImportFinished;
  const { data } = api.endpoints.getUsersImportStatus.useQuery(
    {
      clientId,
      importId: activeImportId ? activeImportId : undefined,
    },
    { pollingInterval: shouldDoPolling ? 2000 : undefined },
  );

  const [downloadErrorList] =
    api.endpoints.downloadImportResultsExcelFile.useLazyQuery();

  const [cancelImportJob] = api.endpoints.cancelImportJob.useMutation();

  const { toast } = useToast();
  const intl = useIntl();

  const [acknowledgeUserImport] =
    api.endpoints.acknowledgeUserImport.useMutation();

  useEffect(() => {
    if (activeImportId && data) {
      const activeImportStatus = data.find(
        (el) => el.importId === activeImportId,
      );
      if (activeImportStatus) {
        setHasActiveImportFinished(
          [
            BulkImportStatusType.Finished,
            BulkImportStatusType.Cancelled,
            BulkImportStatusType.Failed,
          ].includes(activeImportStatus.status),
        );
      } else {
        setHasActiveImportFinished(true);
      }
    }
  }, [data]);

  const onAcknowledge = async (importId: Uuid) => {
    try {
      await acknowledgeUserImport({ clientId, importId }).unwrap();
      toast({
        message: intl.formatMessage({
          id: 'ImportUserStatus.acknowledgeSuccess',
          defaultMessage: 'Acknowledged import job successfully.',
          description: 'Message shown when import job is acknowledged',
        }),
        status: 'success',
      });
    } catch {
      toast({
        message: intl.formatMessage({
          id: 'ImportUserStatus.acknowledgeError',
          defaultMessage: 'Error acknowledging import job',
          description:
            'Message shown when there is an error acknowledging import job',
        }),
        status: 'danger',
      });
    }
  };

  const onDownloadErrorList = async (importId: Uuid) => {
    const timestamp = formatDate(new Date(), 'utc');
    const filename = `evisort-users-${timestamp}.xlsx`;

    const response = await downloadErrorList({
      clientId,
      importId,
      resultType: BulkImportUserResultType.ProcessErrors,
    }).unwrap();
    saveAs(response as unknown as Blob, filename);
  };

  const onCancelImportJob = async (importId: Uuid) => {
    try {
      await cancelImportJob({
        clientId,
        importId,
      });
      toast({
        message: intl.formatMessage({
          id: 'ImportUserStatus.jobCancelled',
          defaultMessage: 'Import job canceled.',
          description: 'Message shown when import job is canceled',
        }),
        status: 'success',
      });
    } catch {
      toast({
        message: intl.formatMessage({
          id: 'ImportUserStatus.cancelError',
          defaultMessage: 'An error occured while cancelling import job.',
          description:
            'Message shown when there is an error cancelling import job',
        }),
        status: 'danger',
      });
    }
  };

  return (
    <Layout direction="column" spacing={2}>
      {data?.map((status) => (
        <ImportStatus
          key={status.id}
          status={status}
          onAcknowledge={() => onAcknowledge(status.importId)}
          onDownloadErrorList={() => onDownloadErrorList(status.importId)}
          onCancelImportJob={() => onCancelImportJob(status.importId)}
        />
      ))}
    </Layout>
  );
};
